import BaseApi from "./base-api";
import HTTPError from "./HTTPError";

/**
 * Class for GoodLeap API
 *
 * @extends BaseApi
 * @description This class is used to interact with the GoodLeap API
 */

class GoodLeapApi extends BaseApi {
  constructor() {
    super("goodleap");

    this.testId = Math.random().toString(36).substring(7);

    this.methods = {
      SUBMIT_LOAN_APPLICATION: "submit_loan_application",
      ADD_GOODLEAP_APP: "add_goodleap_app",
      GET_GOODLEAP_APP_INFO: "get_goodleap_app_info",
      SEND_LOAN_DOCS: "send_loan_docs",
      GET_TPO_V2: "get_tpo_pricing_v2",
      GET_TPO_OFFERS: "get_tpo_offers",
      POST_SHARE_LOAN_APPLICTATION: "share_loan_application",
      SUBMIT_CHANGE_ORDER: "goodleap_product_change_order",
      GET_TPO_DISCLOSURES: "get_tpo_disclosures",
      GET_EXISTING_SAVINGS: "get_existing_savings",
      GET_OFFERS: "get_offers",
      GET_DISCLOSURES: "get_disclosures",
      RESEND_TPO_DOCS: "resend_tpo_docs",
      CHECK_APPLICATION_STATUS: "check_application_status",
    };
  }

  resendTpoDocs = async (payload) => {
    const response = await this.request({
      method: this.methods.RESEND_TPO_DOCS,
      ...payload,
    });

    if (response.success) {
      return response;
    }

    throw new HTTPError(this.status, response.message, this.url);
  };

  getTpoDisclosures = async () => {
    const response = await this.request({
      method: this.methods.GET_TPO_DISCLOSURES,
    });

    if (response.success) return response.data;

    throw new HTTPError(this.status, this.statusText, this.url);
  };
  /**
   * Get a list of disclosures for a goodleap application
   */
  getDisclosures = async () => {
    const response = await this.request({
      method: this.methods.GET_DISCLOSURES,
    });

    if (response.success) {
      return response;
    }

    throw new Error("Could not get GoodLeap disclosures");
  };

  /**
   * Submit a GoodLeap Change Order
   *
   * @param {Object} formState
   * @returns {Promise<Object>} GoodLeap loan application
   */
  submitChangeOrder = async (payload) => {
    const response = await this.request({
      method: this.methods.SUBMIT_CHANGE_ORDER,
      ...payload,
    });

    if (response) {
      return response;
    }

    throw new HTTPError(this.status, this.statusText, this.url);
  };

  getOffers = async () => {
    const response = await this.request({
      method: this.methods.GET_OFFERS,
    });

    if (response.success) {
      return response;
    }

    throw new Error("Could not get GoodLeap offers");
  };

  /**
   * Submit a GoodLeap loan application
   *
   * @param {Object} formState
   * @returns {Promise<Object>} GoodLeap loan application
   */
  submitLoanApplication = async ({ formState }) => {
    const response = await this.request({
      method: this.methods.SUBMIT_LOAN_APPLICATION,
      ...formState,
    });

    if (response.success && response.data) {
      return response.data.data;
    }
    throw new Error(
      response?.message + "Could not submit GoodLeap loan application"
    );
  };

  shareLoanApplication = async (payload) => {
    const response = await this.request({
      method: this.methods.POST_SHARE_LOAN_APPLICTATION,
      ...payload,
    });

    if (response) {
      return response;
    }

    throw new HTTPError(this.status, this.statusText, this.url);
  };

  getTPOPricingV2 = async (payload) => {
    const response = await this.request({
      method: this.methods.GET_TPO_V2,
      ...payload,
    });

    if (response.success) {
      // Include warning from top-level response if present
      if (response.warning) {
        return {
          ...response.data,
          warning: response.warning
        };
      }
      return response.data;
    }

    throw new HTTPError(
      this.status,
      response?.message ? response.message : "Unable to retrieve TPO pricing",
      this.url
    );
  };

  getTPOOffersV2 = async (payload) => {
    const response = await this.request({
      method: this.methods.GET_TPO_OFFERS,
      ...payload,
    });

    if (response.success) {
      return response.data;
    }

    throw new HTTPError(
      this.status,
      this.statusText,
      this.url,
      response.message
    );
  };

  addGoodleapApp = async ({ loanInfo }) => {
    const response = await this.request({
      method: this.methods.ADD_GOODLEAP_APP,
      ...loanInfo,
    });

    if (response.success && response.data) {
      return response.data.data;
    }

    throw new Error(response?.message || "Could not add GoodLeap app");
  };

  getGoodleapAppInfo = async ({ loanId }) => {
    const response = await this.request({
      method: this.methods.GET_GOODLEAP_APP_INFO,
      loanId,
    });

    if (response.success && response.data) {
      return response.data.data;
    }

    throw new Error(response?.message || "Could not get GoodLeap app info");
  };

  sendLoanDocs = async ({ loanId }) => {
    const response = await this.request({
      method: this.methods.SEND_LOAN_DOCS,
      loanId,
    });

    if (response.success && response.data) {
      return response.data.data;
    }

    throw new Error(response?.message || "Could not send GoodLeap loan docs");
  };

  getExistingSavings = async (payload) => {
    const response = await this.request({
      method: this.methods.GET_EXISTING_SAVINGS,
      ...payload,
    });

    if (response) {
      return response;
    }

    throw new HTTPError(this.status, this.statusText, this.url);
  };

  checkApplicationStatus = async (payload) => {
    const response = await this.request({
      method: this.methods.CHECK_APPLICATION_STATUS,
      ...payload,
    });

    if (response) {
      return response;
    }

    throw new HTTPError(this.status, this.statusText, this.url);
  };
}

export default new GoodLeapApi();
